// Which AP scan is which — one matcher, because three surfaces were each guessing.
//
// The scan descriptions AP sends look alike to a careless regex. `/deliver/i` matches
// "Onboard for delivery with driver PARRACASA" just as happily as "Delivered", and on case
// 73698483 that put "Delivered (scanned) · Fri, 4 Sept" on the driver's card and in the
// office drawer for a parcel that was sitting on the van — while the same card asked the
// driver to stop the delivery and bring it back (Paul, 4 Sept 2026). A parcel wrongly shown
// as delivered is the difference between a recoverable RTS and a lost one.
//
// The distinction is tense, not topic: an onboard/out-for-delivery scan says "delivery",
// a completed one says "delivered".
//
// The backend keeps its own copy of these two tests in
// netlify/functions/_shared/case-driver-view.js (Netlify functions are CJS and can't import
// from src/) — change one, change the other.

export function isDeliveredScan(desc) {
  const s = (desc || '').toString();
  // "Onboard for delivery", "Out for delivery", "Attempted delivery" — all "delivery".
  if (!/\bdelivered\b/i.test(s)) return false;
  // A failed-delivery scan is not a delivery.
  if (/\b(not|never|un)\s*delivered\b/i.test(s)) return false;
  return true;
}

export function isOnboardScan(desc) {
  return /\bonboard\b/i.test((desc || '').toString()) || /\bout for delivery\b/i.test((desc || '').toString());
}

// `new Date(null)` is NOT an invalid date — it is epoch 0, and isNaN() waves it through.
// A case with no delivered scan sends `delivered_at: null`, which became a real Date and
// put "Delivered (scanned) · Thu, 1 Jan 1970" on the driver's card for a parcel still on
// the van (case 73732154, 7 Sept 2026) — the same wrong claim isDeliveredScan exists to
// stop, arriving by a different door. Nothing falsy is a date. Exported so the card and the
// office drawer share one hardened parser instead of each keeping a copy of the trap.
export const toDate = (s) => {
  // Falsy covers the lot: null, undefined, '', 0, NaN. A literal 0 is epoch too, and no AP
  // scan happened in 1970 — a zero in a date field is a missing value, not a timestamp.
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

// The most advanced scan state the timeline actually supports, with the date it happened.
// Delivered beats onboard: a parcel that went out and was delivered is delivered.
export function scanState(timeline) {
  const evs = Array.isArray(timeline) ? timeline : [];
  const hit = (test) => {
    const e = evs.find((x) => test((x && x.desc) || '') && toDate(x && x.at));
    return e ? toDate(e.at) : null;
  };
  const delivered = hit(isDeliveredScan);
  if (delivered) return { key: 'delivered', at: delivered };
  const onboard = hit(isOnboardScan);
  if (onboard) return { key: 'onboard', at: onboard };
  return null;
}
